import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import ResizableTable, { ResizableTh, TD_CLASS, TD_NOWRAP } from '../ui/ResizableTable';
import { Alert, btn, ORANGE_HEAD } from '../handover/v2/HoBits';
import { serverMessage } from '../../lib/handoverView';
import {
  createLaboratory, createTestRequest, getTestRegister, listLaboratories,
} from '../../lib/api';
import { defaultRequiredDate, itcLine } from './labView';
import { useProjectPermissions } from '../../hooks/useProjectPermissions';

/**
 * LAB-FE-1 B3 — REQUEST TEST / REQUEST RETEST (prototype `requestDialog`,
 * D20.2 / D20.2').
 *
 * Born on an ITC line (the register's "Test required" row, the ITC page's line
 * footer, or the Accepted · Fail request page) and nowhere else. Test and
 * acceptance come from the line and are SHOWN, never sent: the body is the
 * contract's (`itc_id`, `item_id`, `laboratory_id`, `required_date`,
 * `required_time`, `number_of_tests`, `location`, `comment_to_lab`, `covers`,
 * `retest_of`, `retest_reason`). There is no Standard field (delta a); the
 * line's standard is printed only when the server serves one.
 *
 * "+ Add another asset" offers the lines the register serves in
 * `lines_requiring_request` with the SAME test name — the server's list of
 * lines still owing a request. The door judges again (`COVER_NOT_SAME_TEST`,
 * `LINE_HAS_LIVE_TR`) and its refusal is printed word for word.
 *
 * `retest` ({retest_of, number}) titles the dialog "Request retest" and sends
 * `retest_of`. `reasonRequired` is the line's own `retest_reason_required`: after
 * an Accepted · Fail the next request asks "Why a retest rather than an NCR?"
 * (delta b) — also when the request that followed the Fail was cancelled.
 */

const INPUT = 'w-full bg-[#0b1222] border border-slate-700 rounded-lg px-3 py-2 text-slate-50 text-[13px] [color-scheme:dark]';
const LABEL = `block mb-1.5 ${ORANGE_HEAD}`;
const NEW_LAB = '__new';

const sameLine = (a, b) => a.itc_id === b.itc_id && a.item_id === b.item_id;

function Field({ id, label, req, opt, hint, children }) {
  return (
    <div className="mb-3.5">
      <label className={LABEL} htmlFor={id}>
        {label}
        {req ? <span className="text-red-400 normal-case tracking-normal"> *</span> : null}
        {opt ? <span className="text-slate-500 font-normal normal-case tracking-normal"> (optional)</span> : null}
      </label>
      {hint ? <p className="text-[11.5px] text-slate-500 mb-1.5 mt-0">{hint}</p> : null}
      {children}
    </div>
  );
}

function Kv({ label, children, testId }) {
  return (
    <div className="flex gap-x-3 gap-y-1 items-baseline flex-wrap text-[13px] text-slate-300 py-0.5 mb-2" data-testid={testId}>
      <span className={`${ORANGE_HEAD} min-w-[150px]`}>{label}</span>
      <span>{children}</span>
    </div>
  );
}

export default function RequestTestDialog({
  projectId,
  origin,
  test,
  retest = null,
  reasonRequired = false,
  onClose,
  onIssued,
  testId = 'request-test',
}) {
  // "+ Add a laboratory…" is offered to an Admin / Company Representative
  // (D20.3). A hint only: the door refuses anyone else (LAB_ADD_REQUIRES_ADMIN,
  // printed word for word). Read HERE so no caller page reads the admin flag.
  const canAddLab = !!useProjectPermissions(projectId).isAdmin;
  const [view, setView] = useState('form');
  const [labs, setLabs] = useState([]);
  const [lines, setLines] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [extra, setExtra] = useState([]);
  const [picked, setPicked] = useState([]);
  const [v, setV] = useState({
    lab: '', date: defaultRequiredDate(), time: '', qty: '1', location: '', comment: '', reason: '',
  });
  const [newLab, setNewLab] = useState({ name: '', emails: '', accredited: 'yes' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const set = (k, val) => setV((prev) => ({ ...prev, [k]: val }));

  useEffect(() => {
    let off = false;
    (async () => {
      try {
        const [labRows, reg] = await Promise.all([
          listLaboratories(projectId), getTestRegister(projectId),
        ]);
        if (off) return;
        setLabs(Array.isArray(labRows) ? labRows : []);
        setLines(Array.isArray(reg?.lines_requiring_request) ? reg.lines_requiring_request : []);
      } catch (err) {
        if (!off) setLoadError(serverMessage(err, 'The laboratories could not be loaded.'));
      }
    })();
    return () => { off = true; };
  }, [projectId]);

  // The origin as the register serves it, or as the caller knows it.
  const originRow = useMemo(
    () => ({ ...origin, ...(lines.find((l) => sameLine(l, origin)) || {}) }),
    [lines, origin],
  );
  const covered = useMemo(
    () => [originRow, ...extra.map((k) => lines.find((l) => sameLine(l, k))).filter(Boolean)],
    [originRow, extra, lines],
  );
  const others = useMemo(
    () => lines.filter((l) => l.test_name === test?.name
      && !covered.some((c) => sameLine(c, l))),
    [lines, test, covered],
  );

  const qty = Number(v.qty);
  const canGo = !saving && v.lab && v.lab !== NEW_LAB && v.date
    && Number.isInteger(qty) && qty >= 1 && (!reasonRequired || v.reason.trim());

  const submit = async () => {
    if (!canGo) return;
    setSaving(true);
    setError(null);
    const body = {
      itc_id: origin.itc_id,
      item_id: origin.item_id,
      laboratory_id: v.lab,
      required_date: v.date,
      number_of_tests: qty,
    };
    if (v.time) body.required_time = v.time;
    if (v.location.trim()) body.location = v.location.trim();
    if (v.comment.trim()) body.comment_to_lab = v.comment.trim();
    if (extra.length) body.covers = extra.map((k) => ({ itc_id: k.itc_id, item_id: k.item_id }));
    if (retest?.retest_of) body.retest_of = retest.retest_of;
    if (reasonRequired) body.retest_reason = v.reason.trim();
    try {
      const created = await createTestRequest(projectId, body);
      const lab = labs.find((l) => l.id === v.lab);
      onIssued(created, lab?.name || '');
    } catch (err) {
      setError(serverMessage(err, 'The request could not be issued.'));
      setSaving(false);
    }
  };

  const addLab = async () => {
    const contacts = newLab.emails.split(',').map((e) => e.trim()).filter(Boolean);
    if (!newLab.name.trim() || !contacts.length || saving) return;
    setSaving(true);
    setError(null);
    try {
      const made = await createLaboratory(projectId, {
        name: newLab.name.trim(), contacts, accredited: newLab.accredited === 'yes',
      });
      const rows = await listLaboratories(projectId);
      setLabs(Array.isArray(rows) ? rows : []);
      set('lab', made.id);
      setNewLab({ name: '', emails: '', accredited: 'yes' });
      setView('form');
      const n = made.contact_count ?? contacts.length;
      setError(null);
      setNotice(`Laboratory added · portal link sent to ${n} email${n === 1 ? '' : 's'}`);
    } catch (err) {
      setError(serverMessage(err, 'The laboratory could not be added.'));
    } finally {
      setSaving(false);
    }
  };
  const pickLab = (value) => {
    if (value === NEW_LAB) {
      setError(null);
      setView('lab');
      return;
    }
    set('lab', value);
  };

  const title = view === 'pick' ? 'Which other assets does this test cover?'
    : view === 'lab' ? 'Add a laboratory'
      : (retest ? 'Request retest' : 'Request Test');
  const sub = view === 'pick'
    ? 'Same test, same occasion: one pour across several foundations, compaction tests along several road sections. Only assets whose ITC requires this test and still has no request are listed.'
    : view === 'lab'
      ? 'The emails you list are the only ones that can open this laboratory’s portal for this project.'
      : (retest?.number ? `Retest of ${retest.number}` : '');

  const width = view === 'form' ? 'sm:max-w-[1100px]' : 'sm:max-w-[560px]';

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className={`bg-slate-900 border-slate-700 p-0 gap-0 max-h-screen sm:max-h-[90vh] overflow-y-auto max-w-full h-full sm:h-auto rounded-none sm:rounded-xl ${width}`}
        data-testid={testId}
        aria-describedby={undefined}
      >
        <DialogHeader className="px-4 py-3.5 border-b border-slate-800 text-left">
          <DialogTitle className="text-slate-50 text-base" data-testid={`${testId}-title`}>{title}</DialogTitle>
          {sub ? <p className="text-[12.5px] text-slate-400 mt-1 mb-0">{sub}</p> : null}
        </DialogHeader>

        <div className="px-4 py-3.5">
          {loadError ? <Alert tone="bad" testId={`${testId}-load-error`} className="mb-3">{loadError}</Alert> : null}

          {view === 'form' ? (
            <>
              <Kv label="Test" testId={`${testId}-test`}>
                <b className="text-slate-50">{test?.name || '—'}</b>
                {test?.standard ? <span className="text-slate-500"> · {test.standard}</span> : null}
              </Kv>
              <Kv label="Acceptance" testId={`${testId}-acceptance`}>
                {test?.acceptance || '—'} <span className="text-slate-500">· from the ITP</span>
              </Kv>

              <div className="mb-3.5" data-testid={`${testId}-covers`}>
                <div className="flex items-center gap-2.5 mb-1.5 flex-wrap">
                  <span className={ORANGE_HEAD}>Assets covered by this test request</span>
                  {others.length ? (
                    <span className="ml-auto">
                      <button type="button" className={`${btn.ghost} ${btn.sm}`} data-testid={`${testId}-add-asset`}
                        onClick={() => { setPicked([]); setView('pick'); }}>
                        + Add another asset covered by this test request
                      </button>
                    </span>
                  ) : null}
                </div>
                <div className="bg-[#0d1628] border border-slate-800 rounded-lg px-3 py-2">
                  <ResizableTable testId={`${testId}-covers-table`}>
                    <thead>
                      <tr>
                        <ResizableTh>Asset ID</ResizableTh>
                        <ResizableTh>Asset description</ResizableTh>
                        <ResizableTh>ITC · line</ResizableTh>
                        <ResizableTh>Lot</ResizableTh>
                        <ResizableTh resizable={false}>{''}</ResizableTh>
                      </tr>
                    </thead>
                    <tbody>
                      {covered.map((c, i) => (
                        <tr key={`${c.itc_id}:${c.item_id}`} data-testid={`${testId}-cover-${c.item_id}`}>
                          <td className={TD_NOWRAP}><span className="font-mono text-orange-500 font-bold text-[12px]">{c.asset_number || '—'}</span></td>
                          <td className={`${TD_CLASS} text-slate-50`}>{c.asset_name || '—'}</td>
                          <td className={TD_NOWRAP}>{c.item_no ? itcLine(c.itc_number, c.item_no) : '—'}</td>
                          <td className={TD_NOWRAP}>{c.lot || '—'}</td>
                          <td className={`${TD_NOWRAP} text-right`}>
                            {i === 0 ? <span className="text-slate-500 text-[11.5px]">where you started</span> : (
                              <button type="button" className={btn.text} data-testid={`${testId}-remove-${c.item_id}`}
                                onClick={() => setExtra((prev) => prev.filter((k) => !sameLine(k, c)))}>
                                Remove
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </ResizableTable>
                </div>
                {!others.length ? (
                  <p className="text-[11px] text-slate-500 mt-1.5 mb-0">No other asset with this test is waiting for a request.</p>
                ) : null}
              </div>

              <Field id={`${testId}-lab`} label="Laboratory" req
                hint={canAddLab
                  ? 'Not listed? Pick “+ Add a laboratory”: name and the emails allowed into its portal.'
                  : 'Not listed? Ask your Admin to add it.'}>
                <select id={`${testId}-lab`} data-testid={`${testId}-lab`} className={INPUT} value={v.lab}
                  onChange={(e) => pickLab(e.target.value)}>
                  <option value="">Select a laboratory</option>
                  {labs.map((l) => (
                    <option key={l.id} value={l.id}>{l.name}{l.accredited ? ' · Accredited' : ''}</option>
                  ))}
                  {canAddLab ? <option value={NEW_LAB}>+ Add a laboratory…</option> : null}
                </select>
              </Field>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Field id={`${testId}-date`} label="Required date" req>
                  <input id={`${testId}-date`} data-testid={`${testId}-date`} type="date" className={INPUT}
                    value={v.date} onChange={(e) => set('date', e.target.value)} />
                </Field>
                <Field id={`${testId}-time`} label="Time (optional)">
                  <input id={`${testId}-time`} data-testid={`${testId}-time`} type="time" className={INPUT}
                    value={v.time} onChange={(e) => set('time', e.target.value)} />
                </Field>
                <Field id={`${testId}-qty`} label="Number of tests" req>
                  <input id={`${testId}-qty`} data-testid={`${testId}-qty`} type="number" min="1" step="1" className={INPUT}
                    value={v.qty} onChange={(e) => set('qty', e.target.value)} />
                </Field>
              </div>

              <Field id={`${testId}-location`} label="Location (optional)">
                <input id={`${testId}-location`} data-testid={`${testId}-location`} className={INPUT}
                  placeholder="e.g., Pour P-12 · north-east corner"
                  value={v.location} onChange={(e) => set('location', e.target.value)} />
              </Field>
              <Field id={`${testId}-comment`} label="Comment to the laboratory (optional)">
                <textarea id={`${testId}-comment`} data-testid={`${testId}-comment`} className={`${INPUT} min-h-[70px]`}
                  value={v.comment} onChange={(e) => set('comment', e.target.value)} />
              </Field>
              {reasonRequired ? (
                <Field id={`${testId}-reason`} label="Why a retest rather than an NCR?" req>
                  <textarea id={`${testId}-reason`} data-testid={`${testId}-reason`} className={`${INPUT} min-h-[70px]`}
                    value={v.reason} onChange={(e) => set('reason', e.target.value)} />
                </Field>
              ) : null}
              {notice ? <Alert tone="ok" testId={`${testId}-notice`} className="mt-1">{notice}</Alert> : null}
            </>
          ) : null}

          {view === 'pick' ? (
            <div data-testid={`${testId}-pick`}>
              <span className={LABEL}>Assets <span className="text-red-400 normal-case tracking-normal"> *</span></span>
              {others.map((l) => {
                const on = picked.some((k) => sameLine(k, l));
                return (
                  <label key={`${l.itc_id}:${l.item_id}`}
                    className={`flex gap-2.5 items-start rounded-lg border px-3 py-2.5 mb-1.5 cursor-pointer ${on
                      ? 'border-orange-500/55 bg-orange-500/5' : 'border-slate-700 bg-[#0b1222]'}`}>
                    <input type="checkbox" checked={on} className="mt-1 accent-orange-500"
                      data-testid={`${testId}-pick-${l.item_id}`}
                      onChange={() => setPicked((prev) => (on ? prev.filter((k) => !sameLine(k, l))
                        : [...prev, { itc_id: l.itc_id, item_id: l.item_id }]))} />
                    <span>
                      <b className="block text-slate-50 text-[13px]">{[l.asset_number, l.asset_name].filter(Boolean).join(' — ') || '—'}</b>
                      <span className="block text-slate-400 text-[11.5px]">
                        {[l.item_no ? itcLine(l.itc_number, l.item_no) : null, l.lot ? `lot ${l.lot}` : null].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          ) : null}

          {view === 'lab' ? (
            <div data-testid={`${testId}-newlab`}>
              <Field id={`${testId}-lab-name`} label="Laboratory name" req>
                <input id={`${testId}-lab-name`} data-testid={`${testId}-lab-name`} className={INPUT}
                  placeholder="e.g., SoilTest Pty Ltd" value={newLab.name}
                  onChange={(e) => setNewLab((p) => ({ ...p, name: e.target.value }))} />
              </Field>
              <Field id={`${testId}-lab-emails`} label="Authorised emails" req
                hint="They receive the permanent portal link and, at each visit, a one-time code. An Admin can revoke the link later.">
                <input id={`${testId}-lab-emails`} data-testid={`${testId}-lab-emails`} className={INPUT}
                  placeholder="one or more, separated by commas" value={newLab.emails}
                  onChange={(e) => setNewLab((p) => ({ ...p, emails: e.target.value }))} />
              </Field>
              <Field id={`${testId}-lab-acc`} label="Accreditation">
                <select id={`${testId}-lab-acc`} data-testid={`${testId}-lab-acc`} className={INPUT} value={newLab.accredited}
                  onChange={(e) => setNewLab((p) => ({ ...p, accredited: e.target.value }))}>
                  <option value="yes">Accredited</option>
                  <option value="no">Not accredited</option>
                </select>
              </Field>
            </div>
          ) : null}

          {error ? <Alert tone="bad" testId={`${testId}-error`} className="mt-2">{error}</Alert> : null}
        </div>

        <DialogFooter className="px-4 py-3 border-t border-slate-800 gap-2">
          {view === 'form' ? (
            <>
              <button type="button" onClick={onClose} className={btn.ghost} data-testid={`${testId}-cancel`}>Cancel</button>
              <button type="button" onClick={submit} disabled={!canGo} className={btn.primary} data-testid={`${testId}-ok`}>
                Request Test
              </button>
            </>
          ) : null}
          {view === 'pick' ? (
            <>
              <button type="button" onClick={() => setView('form')} className={btn.ghost} data-testid={`${testId}-pick-cancel`}>Cancel</button>
              <button type="button" disabled={!picked.length} className={btn.primary} data-testid={`${testId}-pick-ok`}
                onClick={() => { setExtra((prev) => [...prev, ...picked]); setView('form'); }}>
                Add
              </button>
            </>
          ) : null}
          {view === 'lab' ? (
            <>
              <button type="button" onClick={() => { setError(null); setView('form'); }} className={btn.ghost} data-testid={`${testId}-lab-cancel`}>Cancel</button>
              <button type="button" onClick={addLab} className={btn.primary} data-testid={`${testId}-lab-ok`}
                disabled={saving || !newLab.name.trim() || !newLab.emails.trim()}>
                Add laboratory
              </button>
            </>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
